import { InputField } from 'fields/base/InputField/InputField';
import { SelectField } from 'fields/SelectField';
import { motion } from 'framer-motion';
import React, { useCallback, useContext, useMemo } from 'react';

import { useThemeColors } from '@/shared/theme';

import {
  DepositWithdrawContext,
  IInnerWallet,
  mapTypeToCaption,
} from 'context/DepositWithdrawContext/DepositWithdrawContext';
import { useDebouncedCallback } from 'hooks/utility';
import { addBignumbers, bn, humanizeBn } from 'tools/math';
import { IDexPair } from 'types/pairs';
import { ITransfer } from 'types/transfers';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { ErrorText } from 'ui/ErrorText/ErrorText';
import { calculateWalletTokenBalance } from 'utils/calculates';

import { Warning } from './components';
import useDeposit from './useDeposit';
import useWithdraw from './useWithdraw';
import { WalletItem } from './WalletItem';

type IFormProps = {
  handleOpenManualTransactionsModal: () => void;
  onRemountTable: () => void;
  onClose: () => void;
  pair: IDexPair;
  onWithdrawResult?: (transfers: ITransfer[]) => void;
};

const Form: React.FC<IFormProps> = ({
  handleOpenManualTransactionsModal,
  onRemountTable,
  onClose,
  pair,
  onWithdrawResult,
}) => {
  const palette = useThemeColors();

  const {
    type,
    values,
    touched,
    setFieldValue,
    validateForm,
    selectedToken,
    token: { human: tokenBalanceHuman },
    innerWallets,
    handleChangeTotalAmount,
    formError,
    confirmedWhitelist,
  } = useContext(DepositWithdrawContext);

  const { deposit, loading: depositLoading } = useDeposit({ onClose, pair });
  const { withdraw, loading: withdrawLoading } = useWithdraw({
    onClose,
    onRemountTable,
    pair,
    onWithdrawResult,
    receiver: values.withdrawReceiver,
  });

  const loading = useMemo(
    () => depositLoading || withdrawLoading || false,
    [withdrawLoading, depositLoading],
  );

  const calculateTotalSendByWalletBalances = useCallback(
    (resultWallets: IInnerWallet[]) => {
      const totalSend = resultWallets.reduce((acc, val) => {
        if (val.balance === '' || Number(val.balance) === 0) return acc;

        return addBignumbers(
          [acc, 18],
          [bn(val.balance, selectedToken.get.decimals), selectedToken.get.decimals],
        );
      }, bn(0));

      setFieldValue('totalSend', humanizeBn(totalSend, 18));
    },
    [selectedToken.get, setFieldValue],
  );

  const calculateTotalSendByWalletBalancesDebounced = useDebouncedCallback(
    calculateTotalSendByWalletBalances,
    300,
  );

  const handleChangeWalletBalance = useCallback(
    (newBalance: string, index: number) => {
      const isBalanceNumeric = newBalance !== '' && !isNaN(Number(newBalance));

      const newWalletBalances = [...innerWallets.get];
      newWalletBalances[index] = {
        ...newWalletBalances[index],
        balance: newBalance,
        max_amount: !!bn(isBalanceNumeric ? newBalance : '0', selectedToken.get.decimals).eq(
          newWalletBalances[index].walletBalance,
        ),
      };

      calculateTotalSendByWalletBalancesDebounced(newWalletBalances);
      innerWallets.set(newWalletBalances);
    },
    [calculateTotalSendByWalletBalancesDebounced, innerWallets, selectedToken.get],
  );

  const selectTokenList = useMemo(
    () => [pair.token_base, pair.token_quote, pair.token_fee],
    [pair],
  );

  const onSubmit = useCallback(async () => {
    const result = await validateForm();

    if (Object.keys(result).length === 0) {
      if (type === 'deposit') {
        deposit();
      } else {
        withdraw();
      }
    }
  }, [type, deposit, withdraw, validateForm]);

  return (
    <div className="mm-deposit-withdraw-modal-form">
      <div className="mm-deposit-withdraw-modal-token-info">
        {type === 'withdraw' && (
          <SelectField
            label="Receiver"
            placeholder="Select receiver"
            items={confirmedWhitelist}
            itemToString={v => v}
            onSelectItem={v => (v ? setFieldValue('withdrawReceiver', v) : undefined)}
            selectedItem={values.withdrawReceiver}
          />
        )}
        <SelectField
          label="Token"
          placeholder="Select token"
          items={selectTokenList}
          itemToString={token => token.symbol}
          onSelectItem={token => (token ? selectedToken.set(token) : undefined)}
          selectedItem={selectedToken.get}
          comment={`Balance: ${tokenBalanceHuman}`}
        />
        <div className="token-total-send-row">
          <InputField
            label="Total amount"
            type="decimal-number"
            value={values.totalSend ?? ''}
            setValue={value => handleChangeTotalAmount(value, values.randPercent ?? '')}
            decimals={selectedToken.get.decimals}
            nodeRight={
              <motion.button
                type="button"
                className="max-button"
                whileTap={{ backgroundColor: palette.light_gray_1 }}
                onClick={() =>
                  handleChangeTotalAmount(
                    humanizeBn(values.balance, selectedToken.get.decimals),
                    values.randPercent ?? '',
                  )
                }
              >
                Max
              </motion.button>
            }
          />

          <InputField
            type="decimal-number"
            value={values.randPercent ?? ''}
            setValue={value =>
              Number(value) <= 100 && handleChangeTotalAmount(values.totalSend ?? '', value)
            }
            decimals={selectedToken.get.decimals}
            nodeRight={<div className="rand-label">Rand. %</div>}
          />
        </div>
      </div>
      <div className="mm-deposit-withdraw-modal-wallets">
        <div className="mm-deposit-withdraw-modal-wallets__header">
          <span>Selected wallets: {innerWallets.get.length}</span>
          <div className="mm-deposit-withdraw-modal-wallets__header__right">
            <span>Amount</span>
          </div>
        </div>
        <ul className="mm-deposit-withdraw-modal-wallets__list scrollable">
          {innerWallets.get.map((wallet, index) => (
            <WalletItem
              key={wallet.id}
              address={wallet.address}
              balance={wallet.balance}
              setBalance={b => handleChangeWalletBalance(b, index)}
              walletBalance={calculateWalletTokenBalance(wallet, selectedToken.get)}
              decimals={selectedToken.get.decimals}
              type={type}
              distributionTouched={!!touched.totalSend}
              disabled={wallet.disabled}
              isValid={wallet.isValid}
            />
          ))}
        </ul>
      </div>
      <Warning />
      <ErrorText>{formError as string}</ErrorText>
      {!loading && (
        <Button type="button" onClick={onSubmit}>
          {mapTypeToCaption(type)}
        </Button>
      )}
      {loading && <ButtonLoading />}
      {!loading && type === 'deposit' && (
        <Button color="transparent" onClick={handleOpenManualTransactionsModal}>
          Manual transactions
        </Button>
      )}
    </div>
  );
};

export { Form };
